'use client';

import React, { useId } from 'react';

import { Divider } from '@packages/shared/src/ui/Divider/Divider';

import type { APlusCluster, APlusItems } from '../../types';
import type { AplusLayoutDefaultProps } from '../types';

import { Item } from '../../atoms/Item/Item.client';
import { useServiceLayer } from '../../hooks/useServiceLayer/useServiceLayer';
import { useAplusData } from '../../hooks/useAplusData/useAplusData';

export type ClusteredProps = {
  clusteredData: APlusCluster;
} & AplusLayoutDefaultProps;

/**
 * Renders APlus services clustered.
 */
export const Clustered = ({
  clusteredData,
  currency,
  triggerOnChangeHandlerOnInit,
  onChangeHandler,
}: ClusteredProps) => {
  const componentId = useId();

  const items: APlusItems = [];
  clusteredData.forEach((cluster) => {
    cluster.items.forEach((item) => {
      items.push(item);
    });
  });

  const { aplusData, handleItemChange } = useAplusData(
    items,
    onChangeHandler,
    triggerOnChangeHandlerOnInit,
  );
  const { setDialogContent, serviceLayer } = useServiceLayer();

  return (
    <>
      <div className="grid grid-cols-[auto_auto_1fr_auto_auto] gap-y-1.5">
        {clusteredData.map((cluster, clusterIndex) => (
          <article
            key={`${componentId}-${cluster.label}-${clusterIndex.toString()}`}
            className="col-span-5 grid grid-cols-subgrid gap-1.5"
          >
            <header className="text-size-body-xs col-span-5 font-bold">{cluster.label}</header>
            {cluster.items.map((item, itemIndex) => {
              const aPlusDataItem = aplusData.get(item.uuid);

              if (!aPlusDataItem) return null;

              const { mandatory, serviceIcon, name, price, free, serviceLayerId, uuid } =
                aPlusDataItem.item;
              const id = `${componentId}-${uuid}`;

              return (
                <React.Fragment key={id}>
                  <Item
                    uuid={uuid}
                    mandatory={mandatory}
                    serviceIcon={serviceIcon}
                    name={name}
                    price={price}
                    free={free}
                    currency={currency}
                    id={id}
                    selected={aplusData.get(uuid)?.selected || false}
                    onInfoClickHandler={() =>
                      setDialogContent({
                        header: name || '',
                        serviceLayerId,
                      })
                    }
                    onItemChangeHandler={handleItemChange}
                  />
                  {itemIndex < cluster.items.length - 1 && <Divider className="col-span-5" />}
                </React.Fragment>
              );
            })}
            {clusterIndex < clusteredData.length - 1 && <Divider className="col-span-5" />}
          </article>
        ))}
      </div>
      {serviceLayer}
    </>
  );
};
